<template>
  <div ref="frame" class="monaco-editor-frame" :style="frameStyle">
    <div ref="container" class="monaco-editor-host"></div>
  </div>
</template>

<script>
/**
 * Monaco-based code pane (Vue 2 safe — no optional chaining).
 * Height comes from the frame (flex parent or explicit px). The inner host is
 * absolutely filled so Monaco cannot grow the page.
 */
import {
  registerSchemaYamlCompletions,
  setSchemaCompletionContext,
  clearSchemaCompletionContext,
} from "@/components/shared/schemaYamlCompletions.js"
import {
  registerScriptSdkCompletions,
  setScriptSdkDialect,
  clearScriptSdkDialect,
} from "@/components/cases/case-config/scripts/scriptSdkCompletions.js"

export default {
  name: "edit-code-pane",
  props: {
    code: { type: String, default: "" },
    language: { type: String, default: "javascript" },
    // "100%" fills a sized flex parent (Script IDE, schema shell).
    // Numeric px is for auto-height pages (emails, account features).
    height: { type: [String, Number], default: 400 },
    readOnly: { type: Boolean, default: false },
    schemaIntellisense: { type: Boolean, default: false },
    schemaCompletions: { type: Object, default: null },
    // FaaS dialect for cb/logger completions: javascript|python|ruby|go|rust.
    // Empty for Expressive5, Agentic, JSON/YAML panes.
    scriptSdkDialect: { type: String, default: "" },
  },
  data: function () {
    return {
      editor: null,
      monaco: null,
      suppressEmit: false,
      resizeObserver: null,
      intersectionObserver: null,
      lastLayoutWidth: 0,
      lastLayoutHeight: 0,
    }
  },
  computed: {
    fillParent: function () {
      return this.height === "100%" || this.height === "100"
    },
    frameStyle: function () {
      var h = this.height
      if (this.fillParent) {
        return { height: "100%", width: "100%", flex: "1 1 auto", minHeight: "0" }
      }
      if (typeof h === "number" || (typeof h === "string" && /^\d+$/.test(h))) {
        return { height: Number(h) + "px", width: "100%" }
      }
      return { height: h, width: "100%", minHeight: "200px" }
    },
  },
  watch: {
    code: function (val) {
      if (!this.editor) return
      var current = this.editor.getValue()
      if (val !== current) {
        this.suppressEmit = true
        this.editor.setValue(val || "")
        this.suppressEmit = false
      }
    },
    language: function (lang) {
      if (!this.editor || !this.monaco) return
      this.monaco.editor.setModelLanguage(this.editor.getModel(), this.mapLanguage(lang))
    },
    scriptSdkDialect: function (dialect) {
      if (!this.editor) return
      console.debug("[script-sdk] dialect set to", dialect || "(none)")
      setScriptSdkDialect(this.editor.getModel(), dialect || null)
    },
    readOnly: function (ro) {
      if (this.editor) {
        this.editor.updateOptions({ readOnly: !!ro })
      }
    },
    schemaCompletions: {
      handler: function (ctx) {
        if (this.editor) setSchemaCompletionContext(this.editor.getModel(), ctx)
      },
      deep: true,
    },
  },
  mounted: function () {
    this.initEditor()
  },
  beforeDestroy: function () {
    window.removeEventListener("resize", this.handleResize)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
    if (this.editor) {
      var model = this.editor.getModel()
      clearSchemaCompletionContext(model)
      clearScriptSdkDialect(model)
      this.editor.dispose()
      this.editor = null
      if (model) model.dispose()
    }
  },
  methods: {
    mapLanguage: function (lang) {
      var l = (lang || "javascript").toLowerCase()
      if (l === "js" || l === "javascript" || l === "typescript") return l === "typescript" ? "typescript" : "javascript"
      if (l === "py" || l === "python") return "python"
      if (l === "rb" || l === "ruby") return "ruby"
      if (l === "go" || l === "golang") return "go"
      if (l === "rs" || l === "rust") return "rust"
      if (l === "java") return "java"
      if (l === "cs" || l === "csharp" || l === "dotnet" || l === "c#") return "csharp"
      if (l === "yaml" || l === "yml") return "yaml"
      if (l === "json") return "json"
      if (l === "text" || l === "plaintext" || l === "agentic" || l === "agent") return "plaintext"
      return "javascript"
    },
    layoutToHost: function () {
      if (!this.editor || !this.$refs.frame) return
      var el = this.$refs.frame
      var width = el.clientWidth
      var height = el.clientHeight
      if (width < 2 || height < 2) return
      if (width === this.lastLayoutWidth && height === this.lastLayoutHeight) return
      this.lastLayoutWidth = width
      this.lastLayoutHeight = height
      this.editor.layout({ width: width, height: height })
    },
    handleResize: function () {
      this.layoutToHost()
    },
    observeHost: function () {
      var self = this
      var el = self.$refs.frame
      if (!el) return
      if (typeof ResizeObserver !== "undefined") {
        self.resizeObserver = new ResizeObserver(function () {
          self.layoutToHost()
        })
        self.resizeObserver.observe(el)
      }
      if (typeof IntersectionObserver !== "undefined") {
        self.intersectionObserver = new IntersectionObserver(function (entries) {
          var i
          for (i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              self.lastLayoutWidth = 0
              self.lastLayoutHeight = 0
              self.layoutToHost()
            }
          }
        })
        self.intersectionObserver.observe(el)
      }
      window.addEventListener("resize", self.handleResize)
    },
    ensureMonacoEnv: function () {
      if (typeof window === "undefined") return
      if (window.MonacoEnvironment) return
      window.MonacoEnvironment = {
        getWorker: function () {
          return {
            postMessage: function () {},
            addEventListener: function () {},
            removeEventListener: function () {},
            terminate: function () {},
          }
        },
      }
    },
    initEditor: function () {
      var self = this
      self.ensureMonacoEnv()
      Promise.all([
        // edcore.main = editor API + suggest/hover/snippets. Not editor.main,
        // which also boots the TypeScript language worker (stubbed here).
        import("monaco-editor/esm/vs/editor/edcore.main"),
        import("monaco-editor/min/vs/editor/editor.main.css"),
        import("monaco-editor/esm/vs/basic-languages/javascript/javascript.contribution"),
        import("monaco-editor/esm/vs/basic-languages/typescript/typescript.contribution"),
        import("monaco-editor/esm/vs/basic-languages/python/python.contribution"),
        import("monaco-editor/esm/vs/basic-languages/ruby/ruby.contribution"),
        import("monaco-editor/esm/vs/basic-languages/go/go.contribution"),
        import("monaco-editor/esm/vs/basic-languages/rust/rust.contribution"),
        import("monaco-editor/esm/vs/basic-languages/yaml/yaml.contribution"),
        import("monaco-editor/esm/vs/language/json/monaco.contribution"),
      ])
        .then(function (mods) {
          return mods[0]
        })
        .then(function (monaco) {
          self.monaco = monaco
          if (!self.$refs.container) return
          var language = self.mapLanguage(self.language)
          var model = null
          var options = {
            theme: "vs",
            automaticLayout: false,
            minimap: { enabled: false },
            fontSize: 13,
            tabSize: 2,
            readOnly: !!self.readOnly,
            scrollBeyondLastLine: false,
            wordWrap: "on",
            quickSuggestions: true,
            suggestOnTriggerCharacters: true,
            wordBasedSuggestions: !self.schemaIntellisense,
            // Widgets attach to body (frame overflow is hidden); style them globally.
            fixedOverflowWidgets: true,
          }
          if (self.schemaIntellisense) {
            registerSchemaYamlCompletions(monaco)
            model = monaco.editor.createModel(
              self.code || "",
              "yaml",
              monaco.Uri.parse("inmemory://cb/case-schema-" + self._uid + ".yaml")
            )
            setSchemaCompletionContext(model, self.schemaCompletions)
            options.model = model
          } else {
            options.value = self.code || ""
            options.language = language
          }
          self.editor = monaco.editor.create(self.$refs.container, options)
          if (!self.schemaIntellisense) {
            registerScriptSdkCompletions(monaco)
            setScriptSdkDialect(self.editor.getModel(), self.scriptSdkDialect || null)
            console.debug("[script-sdk] editor ready", {
              language: language,
              dialect: self.scriptSdkDialect || "(none)"
            })
          } else {
            console.debug("[schema-yaml] editor ready")
          }
          self.editor.onDidChangeModelContent(function () {
            if (self.suppressEmit) return
            self.$emit("update-code", self.editor.getValue())
          })
          self.observeHost()
          self.$nextTick(function () {
            self.layoutToHost()
          })
          self.$emit("ready", self.editor)
        })
        .catch(function (err) {
          console.error("Failed to load Monaco", err)
        })
    },
    layout: function () {
      this.lastLayoutWidth = 0
      this.lastLayoutHeight = 0
      this.layoutToHost()
    },
  },
}
</script>

<style scoped>
.monaco-editor-frame {
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
}
.monaco-editor-host {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  box-sizing: border-box;
}
</style>

<!-- Unscoped: suggest/hover widgets attach to document.body, so scoped CSS never reaches them. -->
<style>
.monaco-editor .suggest-widget,
.monaco-editor .suggest-details,
.editor-widget.suggest-widget,
.suggest-widget {
  background: #ffffff !important;
  background-color: #ffffff !important;
  color: #222222 !important;
  border: 1px solid #c5c5c5 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}
.monaco-editor .suggest-widget .monaco-list,
.monaco-editor .suggest-widget .monaco-list .monaco-list-rows,
.monaco-editor .suggest-widget .monaco-list .monaco-list-row,
.suggest-widget .monaco-list,
.suggest-widget .monaco-list .monaco-list-rows,
.suggest-widget .monaco-list .monaco-list-row {
  background: #ffffff !important;
  background-color: #ffffff !important;
  color: #222222 !important;
}
.monaco-editor .suggest-widget .monaco-list .monaco-list-row.focused,
.monaco-editor .suggest-widget .monaco-list .monaco-list-row:hover,
.suggest-widget .monaco-list .monaco-list-row.focused,
.suggest-widget .monaco-list .monaco-list-row:hover {
  background: #e8f0fe !important;
  background-color: #e8f0fe !important;
  color: #222222 !important;
}
.monaco-editor .suggest-widget .monaco-highlighted-label,
.monaco-editor .suggest-widget .details-label,
.suggest-widget .monaco-highlighted-label,
.suggest-widget .details-label {
  color: #222222 !important;
}
.monaco-hover,
.monaco-editor-hover {
  background: #ffffff !important;
  background-color: #ffffff !important;
  color: #222222 !important;
  border: 1px solid #c5c5c5 !important;
}
</style>
